import { isNonEmpty } from '@sgme/fp';
import { Collapse } from '@sgme/ui';
import classNames from 'classnames';
import { AccordionSection } from 'components/common-form/sidePanel/AccordionSection';
import { CopyAllButtonByExcelGenerator } from 'components/shared/CopyAllButton/CopyAllButton';
import { OpenInModalContent } from 'components/shared/OpenInModal/OpenInModalContent';
import { FormattedValue } from 'components/shared/ReadOnlyGrid/FormattedValue';
import { useFormatMatrix, useGetSelectedUnitIdx, useGreeksTitle } from 'components/shared/Sensitivity/Greeks/hooks';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { FICCGreek } from 'state/quotes/details/details.model';
import { useToggle } from 'usehooks-ts';

export type GreeksTablesProps = {
  greeks: readonly FICCGreek[];
  greeksPrecision: number;
};

export function GreeksTablesMenu({ greeks, greeksPrecision }: GreeksTablesProps) {
  return (
    <div className="row">
      {greeks.map(greek => {
        return <GreekAccordion key={greek.title} greek={greek} greeksPrecision={greeksPrecision} />;
      })}
    </div>
  );
}
type GreekAccordionProps = { greek: FICCGreek; greeksPrecision: number };
function GreekAccordion({ greek, greeksPrecision }: GreekAccordionProps) {
  const isInternal = useIsInternalUser();
  const [showGreek, toggleShowGreek] = useToggle(isInternal ? false : true);
  const [isOpen, toggleModalOpen] = useToggle();
  const formatGreeksTitle = useGreeksTitle();
  const title = formatGreeksTitle(greek, greeksPrecision);
  return (
    <Collapse
      fill="fill"
      icon="end"
      size="sm"
      activeKey={showGreek ? 'ficc_greeks' : undefined}
      onSelect={_ => toggleShowGreek()}
    >
      <AccordionSection
        section="ficc_greeks"
        getTitle={() => title}
        e2e="ficc_greeks"
        openModal={toggleModalOpen}
        showSpinners={false}
      >
        <OpenInModalContent isModalOpen={isOpen} modalTitle={title} toggleModal={toggleModalOpen}>
          {greek.matrix.length > 0 ? <GreekSection {...greek} greeksPrecision={greeksPrecision} /> : undefined}
        </OpenInModalContent>
      </AccordionSection>
    </Collapse>
  );
}

type GreekUnitsButtonsProps = {
  units: readonly string[];
  selectedUnit: string;
  onSelect: (unit: string) => void;
};

function GreekUnitsButtons({ units, selectedUnit, onSelect }: GreekUnitsButtonsProps) {
  return units.map(unit => (
    <button
      key={unit}
      className={classNames('btn btn-link', { 'text-info': unit === selectedUnit })}
      onClick={() => onSelect(unit)}
    >
      <FormattedMessage id="sidePanel.tabs.greeks.value" values={{ unit }} />
    </button>
  ));
}
function GreekSection({
  greeksPrecision,
  showTitle = false,
  ...greek
}: FICCGreek & { greeksPrecision: number; showTitle?: boolean; toggleModal?: () => void; isModalOpen?: boolean }) {
  const formatGreeksTitle = useGreeksTitle();
  const [selectedUnit, selectUnit] = useState<string>(greek.units[0]);
  const selectedUnitIndex = useGetSelectedUnitIdx(selectedUnit, greek.units);
  const title = formatGreeksTitle({ ...greek, units: [selectedUnit] }, greeksPrecision, selectedUnitIndex);
  return (
    <>
      {/* <OpenInModalContent isOpen={isModalOpen} toggle={toggleModal} modalTitle={title}> */}
      <div key={title}>
        {showTitle && <h6 className="card-title py-2 text-secondary">{title}</h6>}
        {greek.units.length > 1 ? (
          <div className="w-100 d-flex justify-content-end" data-e2e="ficcGreeksUnits">
            <GreekUnitsButtons units={greek.units} selectedUnit={selectedUnit} onSelect={selectUnit} />
          </div>
        ) : undefined}
        {greek.matrix.length > 0 ? (
          <div className="col ">
            <GreeksTable {...greek} greeksPrecision={greeksPrecision} selectedUnitIndex={selectedUnitIndex} />
          </div>
        ) : undefined}
      </div>
      {/* </OpenInModalContent> */}
    </>
  );
}
export function GreeksTables({ greeks, greeksPrecision }: GreeksTablesProps) {
  return (
    <div className="d-flex flex-column flex-grow-1 px-3  py-2">
      {greeks.map(greek => (
        <GreekSection {...greek} greeksPrecision={greeksPrecision} showTitle key={greek.title} />
      ))}
    </div>
  );
}
type GreeksTableProps = {
  greeksPrecision: number;
  selectedUnitIndex: number;
} & FICCGreek;

const GreeksTable: FC<GreeksTableProps> = ({ greeksPrecision, selectedUnitIndex, ...greek }) => {
  const formatMatrixContent = useFormatMatrix(greek, greeksPrecision);
  const { header, content } = formatMatrixContent(selectedUnitIndex);
  const valuesColsCount = header.filter(isNonEmpty).length || 1;
  const generateFormattedGrid = useCallback(() => {
    let res: (string | null)[][] = [];
    for (let i = 0; i < greek.units.length; i++) {
      const { title, header, content } = formatMatrixContent(i);
      res = res.concat([title, header, ...content]);
    }
    return res;
  }, [formatMatrixContent, greek.units]);
  const table = (
    <div className=" overflow-x-auto">
      <table className="table table-sm table-bordered table-striped my-0 p-3">
        <thead>
          {header.map(cell => (
            <th key={cell} className="text-center">
              {cell}
            </th>
          ))}
        </thead>
        <tbody>
          <GreeksMatrixBody
            matrix={content}
            valuesColsCount={valuesColsCount}
            greeksPrecision={greeksPrecision}
            unitsLength={greek.units.length}
          />
        </tbody>
      </table>
    </div>
  );
  return (
    <>
      {table}
      <CopyAllButtonByExcelGenerator className="float-end" generateExcelGrid={generateFormattedGrid} />
    </>
  );
};

type GreeksMatrixBodyProps = {
  matrix: (string | null)[][];
  valuesColsCount: number;
  greeksPrecision: number;
  unitsLength: number;
};
const GreeksMatrixBody: FC<GreeksMatrixBodyProps> = ({ matrix, valuesColsCount, greeksPrecision, unitsLength }) => {
  return (
    <>
      {matrix.map((row, rowIdx) => (
        <tr key={rowIdx}>
          {row.map((col, colIdx) => {
            if (colIdx >= row.length - valuesColsCount) {
              return (
                <GreeksValueCell key={col} greeksPrecision={greeksPrecision} col={col} unitsLength={unitsLength} />
              );
            } else if (col === null) {
              return null;
            }
            return (
              <td key={col} rowSpan={getRowSpan(matrix, rowIdx, colIdx)}>
                {col}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
};

type GreeksValueCellProps = {
  col: string | null;
  greeksPrecision: number;
  unitsLength: number;
};
function GreeksValueCell({ col, greeksPrecision, unitsLength }: GreeksValueCellProps) {
  const colValuesByUnits = col === null ? [null] : col.split(',');
  return colValuesByUnits.slice(0, unitsLength).map(colValue => (
    <td className="text-end" key={col}>
      {colValue !== null && <FormattedValue value={parseFloat(colValue)} precision={greeksPrecision} />}
    </td>
  ));
}

const getRowSpan = (matrix: any[][], rowIdx: number, colIdx: number) => {
  if (matrix[rowIdx][colIdx] === null || rowIdx === matrix.length - 1) {
    return;
  }
  const initialRowIdx = rowIdx;
  rowIdx += 1;
  while (rowIdx < matrix.length && !matrix[rowIdx][colIdx]) {
    rowIdx += 1;
  }
  return rowIdx - initialRowIdx;
};
